import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  Table,
  Button,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Space,
  Tag,
  Input,
  message,
} from "antd";
import {
  FileExcelOutlined,
  ReloadOutlined,
  SearchOutlined,
  PercentageOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { getInformeEntregasByProductoPO } from "../../../services/entregas/servicesOrdenesCompra";

const { RangePicker } = DatePicker;
const { Option } = Select;

const InformeEntregasPO = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  // Estados para filtros
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [avanceFilter, setAvanceFilter] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await getInformeEntregasByProductoPO();
      if (result.status === 200) {
        setData(result.data);
        setFilteredData(result.data);
        message.success("Datos cargados correctamente");
      } else {
        message.error(result.message || "Error al cargar datos");
      }
    } catch (error) {
      message.error("Error al obtener los datos");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Obtener órdenes únicas
  const ordenes = useMemo(() => {
    return [...new Set(data.map((item) => item.numero_orden))];
  }, [data]);

  // Aplicar filtros
  useEffect(() => {
    let filtered = [...data];

    if (selectedOrden) {
      filtered = filtered.filter((item) => item.numero_orden === selectedOrden);
    }

    if (searchText) {
      filtered = filtered.filter((item) =>
        item.productoPO.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (dateRange && dateRange[0] && dateRange[1]) {
      filtered = filtered.filter((item) => {
        const fecha = dayjs(item.fechaEntrega);
        return fecha.isAfter(dateRange[0]) && fecha.isBefore(dateRange[1]);
      });
    }

    if (avanceFilter) {
      filtered = filtered.filter((item) => {
        const avance = parseFloat(item.porcentaje_avance);
        if (avanceFilter === "completo") return avance === 100;
        if (avanceFilter === "parcial") return avance > 0 && avance < 100;
        if (avanceFilter === "pendiente") return avance === 0;
        return true;
      });
    }

    setFilteredData(filtered);
  }, [selectedOrden, searchText, dateRange, avanceFilter, data]);

  // Calcular indicadores
  const indicadores = useMemo(() => {
    const totalOrdenes = new Set(filteredData.map((item) => item.numero_orden))
      .size;
    const totalProductos = filteredData.length;
    const totalSolicitado = filteredData.reduce(
      (sum, item) => sum + item.cantidad_solicitada,
      0
    );
    const totalEntregado = filteredData.reduce(
      (sum, item) => sum + parseFloat(item.cantidad_entregada),
      0
    );
    const avancePromedio =
      filteredData.length > 0
        ? filteredData.reduce(
            (sum, item) => sum + parseFloat(item.porcentaje_avance),
            0
          ) / filteredData.length
        : 0;

    return {
      totalOrdenes,
      totalProductos,
      totalSolicitado,
      totalEntregado,
      avancePromedio: avancePromedio.toFixed(2),
    };
  }, [filteredData]);

  const exportToExcel = () => {
    const headers = [
      "No. Orden",
      "Producto",
      "Unidad",
      "Cant. Solicitada",
      "Cant. Entregada",
      "% Avance",
      "Fecha Estimada",
      "Fecha Entrega",
      "Diferencia Días",
      "Estado", // Nueva columna
    ];

    const csvContent = [
      headers.join(","),
      ...filteredData.map((row) => {
        return [
          row.numero_orden,
          `"${row.productoPO}"`,
          row.Unidad_medida,
          row.cantidad_solicitada,
          row.cantidad_entregada,
          row.porcentaje_avance,
          dayjs(row.fechaEstimada).format("DD/MM/YYYY"),
          dayjs(row.fechaEntrega).format("DD/MM/YYYY"),
          row.diferencia_dias,
          row.estado, // agregar estado en la fila
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `informe_entregas_${dayjs().format("YYYYMMDD")}.xlsx`
    );
    link.click();
    message.success("Archivo exportado correctamente");
  };

  const columns = [
    {
      title: "No. Orden",
      dataIndex: "numero_orden",
      key: "numero_orden",
      width: 120,
      fixed: "left",
    },
    {
      title: "Producto",
      dataIndex: "productoPO",
      key: "productoPO",
      width: 300,
      ellipsis: true,
    },
    {
      title: "Unidad",
      dataIndex: "Unidad_medida",
      key: "Unidad_medida",
      width: 120,
    },
    {
      title: "Cant. Solicitada",
      dataIndex: "cantidad_solicitada",
      key: "cantidad_solicitada",
      width: 130,
      align: "right",
      render: (val) => val.toLocaleString(),
    },
    {
      title: "Cant. Entregada",
      dataIndex: "cantidad_entregada",
      key: "cantidad_entregada",
      width: 130,
      align: "right",
      render: (val) => parseFloat(val).toLocaleString(),
    },
    {
      title: "% Avance",
      dataIndex: "porcentaje_avance",
      key: "porcentaje_avance",
      width: 120,
      align: "center",
      render: (val) => {
        const percent = parseFloat(val);
        let color = "red";
        if (percent === 100) color = "green";
        else if (percent >= 50) color = "orange";
        return <Tag color={color}>{percent}%</Tag>;
      },
    },
    {
      title: "Fecha Estimada",
      dataIndex: "fechaEstimada",
      key: "fechaEstimada",
      width: 130,
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Fecha Entrega",
      dataIndex: "fechaEntrega",
      key: "fechaEntrega",
      width: 130,
      render: (date) => dayjs(date).format("DD/MM/YYYY"),
    },
    {
      title: "Diferencia (días)",
      dataIndex: "diferencia_dias",
      key: "diferencia_dias",
      width: 140,
      align: "center",
      render: (val) => {
        const color = val > 0 ? "red" : val < 0 ? "green" : "blue";
        return <Tag color={color}>{val > 0 ? `+${val}` : val}</Tag>;
      },
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 120,
      align: "center",
      render: (estado) => {
        let color = "blue";
        if (estado === "Cumplido") color = "green";
        if (estado === "Retrasado") color = "red";

        return <Tag color={color}>{estado}</Tag>;
      },
    },
  ];

  const clearFilters = () => {
    setSelectedOrden(null);
    setSearchText("");
    setDateRange(null);
    setAvanceFilter(null);
  };

  return (
    <div style={{ padding: "24px", background: "#f0f2f5", minHeight: "100vh" }}>
      <Card
        title={
          <span style={{ fontSize: "20px", fontWeight: "bold" }}>
            📊 Informe de Entregas vs Productos PO
          </span>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchData}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={exportToExcel}
              disabled={filteredData.length === 0}
            >
              Exportar Excel
            </Button>
          </Space>
        }
      >
        {/* Tarjetas de Indicadores */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Órdenes"
                value={indicadores.totalOrdenes}
                prefix={<ShoppingCartOutlined />}
                valueStyle={{ color: "#1890ff" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Total Productos"
                value={indicadores.totalProductos}
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: "#52c41a" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Cantidad Entregada"
                value={indicadores.totalEntregado}
                suffix={`/ ${indicadores.totalSolicitado}`}
                valueStyle={{ color: "#faad14" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Avance Promedio"
                value={indicadores.avancePromedio}
                prefix={<PercentageOutlined />}
                suffix="%"
                valueStyle={{ color: "#722ed1" }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filtros */}
        <Card title="Filtros" size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Seleccionar Orden"
                style={{ width: "100%" }}
                value={selectedOrden}
                onChange={setSelectedOrden}
                allowClear
              >
                {ordenes.map((orden) => (
                  <Option key={orden} value={orden}>
                    {orden}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Input
                placeholder="Buscar producto..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                style={{ width: "100%" }}
                value={dateRange}
                onChange={setDateRange}
                format="DD/MM/YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Estado de avance"
                style={{ width: "100%" }}
                value={avanceFilter}
                onChange={setAvanceFilter}
                allowClear
              >
                <Option value="completo">Completo (100%)</Option>
                <Option value="parcial">Parcial (1-99%)</Option>
                <Option value="pendiente">Pendiente (0%)</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={2}>
              <Button onClick={clearFilters} block>
                Limpiar
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey={(record) => `${record.id_entrega}-${record.productoPO}`}
          loading={loading}
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} registros`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      </Card>
    </div>
  );
};

export default InformeEntregasPO;
